// AFTER FOUR v47 — Shopify-compatible async purchase actions and mobile PDP CTA.
import { CartLinesUpdateEvent } from '@shopify/events';

(() => {
  'use strict';
  const selectors = '.af181-form, .af18-form, .af212-card__form, .af15-form';
  const root = window.Shopify?.routes?.root || '/';
  const cartPage = root + 'cart';
  const shippingLimit = 19900; // ₹40 standard shipping below ₹199; free from ₹199.
  const money = (paise) => '₹' + (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  let noticeTimer;

  const toast = document.createElement('div');
  toast.className = 'af47-toast';
  toast.dataset.open = 'false';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.innerHTML = '<span class="af47-toast__message"></span><a class="af47-toast__cta" href="' + cartPage + '">VIEW BAG →</a>';
  const say = (message, isError = false) => {
    toast.querySelector('.af47-toast__message').textContent = message;
    toast.querySelector('.af47-toast__cta').hidden = isError;
    toast.dataset.open = 'true';
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => { toast.dataset.open = 'false'; }, 5600);
  };

  const setupPdp = () => {
    const nudge = document.querySelector('[data-af47-pdp-shipping]');
    const form = document.querySelector('.af15-form');
    const bar = document.querySelector('[data-af47-mobile-pdp-bar]');
    if (!form || !bar) return;
    const mainButton = form.querySelector('button.af15-atc');
    const stickyButton = bar.querySelector('[data-af47-sticky-action]');
    const stickyPrice = bar.querySelector('[data-af47-sticky-price]');

    const updateShipping = () => {
      const paise = Number(nudge?.dataset.unitPaise) || 0;
      const qty = Math.max(1, Number(form.querySelector('input[name="quantity"]')?.value || 1));
      // Shipping is determined by the full cart after discounts, not only this product.
      const txt = nudge?.querySelector('[data-af47-shipping-copy]');
      if (txt) txt.textContent = '₹40 delivery below ₹199 · Free delivery on ₹199+ orders.';
      if (stickyPrice) stickyPrice.textContent = document.querySelector('.af15-price strong')?.textContent || '';
      if (stickyButton && mainButton) {
        stickyButton.disabled = mainButton.disabled;
        stickyButton.textContent = mainButton.disabled ? 'SOLD OUT' : 'ADD TO BAG →';
      }
    };
    form.addEventListener('input', (event) => {
      if (event.target?.name === 'quantity') updateShipping();
    });
    document.addEventListener('variant:change', (event) => {
      if (nudge && event.detail?.price) nudge.dataset.unitPaise = String(event.detail.price);
      updateShipping();
    });
    stickyButton?.addEventListener('click', () => {
      if (!mainButton?.disabled && !form.dataset.af47Busy) {
        form.requestSubmit(mainButton);
      }
    });
    let ticking = false;
    const toggleBar = () => {
      ticking = false;
      if (!mainButton) return;
      const pastButton = mainButton.getBoundingClientRect().bottom < 82 && window.scrollY > 140;
      const mobile = window.matchMedia('(max-width:749px)').matches;
      bar.dataset.active = pastButton && mobile ? 'true' : 'false';
    };
    const scheduleToggle = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(toggleBar);
    };
    window.addEventListener('scroll', scheduleToggle, { passive: true });
    window.addEventListener('resize', scheduleToggle);
    updateShipping();
    scheduleToggle();
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.documentElement.classList.add('af47-js');
    document.body.appendChild(toast);
    setupPdp();
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target instanceof Element ? event.target.closest(selectors) : null;
    if (!form) return;
    if (event.defaultPrevented) return; // Another theme purchase handler has priority.
    if (!form.reportValidity()) return;

    const button = event.submitter instanceof HTMLElement
      ? event.submitter
      : form.querySelector('button[type="submit"]');
    const variantId = form.querySelector('input[name="id"]')?.value;
    const quantity = Math.max(1, Number(form.querySelector('input[name="quantity"]')?.value || 1));
    if (!variantId || !button || button.disabled || form.dataset.af47Busy) return;

    event.preventDefault();
    form.dataset.af47Busy = '1';
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'ADDING...';

    const payload = new FormData(form);
    const sections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.dataset.sectionId).filter(Boolean);
    if (sections.length) payload.set('sections', [...new Set(sections)].join(','));
    payload.set('sections_url', window.location.pathname);

    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    }));

    let didAdd = false;
    try {
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST', body: payload, credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      const addResult = await response.json();
      if (!response.ok || addResult.status) {
        const reason = addResult.description || addResult.message || 'Could not add this pack. Please try again.';
        throw new Error(String(reason));
      }
      didAdd = true;
      const cartResponse = await fetch(root + 'cart.js', {
        credentials: 'same-origin', headers: { Accept: 'application/json' }
      });
      if (!cartResponse.ok) throw new Error('Pack added, but the cart needs refreshing. Open your bag to continue.');
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: addResult.sections,
          source: 'after-four-v47',
          didError: false
        }
      });
      const icon = document.querySelector('cart-icon');
      if (icon && typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count, false);
      const gap = Math.max(0, shippingLimit - cart.total_price);
      const note = gap > 0
        ? 'Added! ₹40 delivery under ₹199. Add ' + money(gap) + ' for free delivery.'
        : 'Added! Free delivery unlocked on orders ₹199 and above.';
      say(note);
      document.dispatchEvent(new CustomEvent('af51:cart-updated', { detail: { cart } }));
      button.textContent = 'ADDED ✓';
      // On the full cart page, a newly added line must update the entire order summary.
      // Reload only after Shopify has confirmed the addition; don't double-submit.
      if (form.closest('.af50-cart-upsell')) {
        window.setTimeout(() => window.location.reload(), 420);
      }
    } catch (error) {
      deferred.reject(error);
      // Do not repeat the request: a server failure can be ambiguous.
      if (didAdd) {
        say('Pack added! Open your bag to see your updated cart.');
        button.textContent = 'ADDED ✓';
      } else {
        say(error.message || 'Could not add this pack. Please try again.', true);
        button.textContent = 'TRY AGAIN';
      }
      console.warn('[AF47] Add to bag:', error);
    } finally {
      setTimeout(() => {
        button.textContent = original;
        button.disabled = false;
        delete form.dataset.af47Busy;
      }, 1700);
    }
  });
})();
